/**
 * 인증(Auth) 관련 API 서비스 (관리자)
 */

import baseApiClient from '@cnd_fe/ky-client'

// 인증 서버 URL (환경변수)
const authServer = import.meta.env.VITE_AUTH_SERVER_URL || ''
const clientId = 'res-admin'

// Auth API 클라이언트 (일반)
const authClient = baseApiClient.extend({
  prefixUrl: `${authServer}/api/v1/res/auth`
})

// Auth 서버 클라이언트 (토큰 교환 등)
const authServerClient = baseApiClient.extend({
  prefixUrl: authServer
})

/**
 * 커스텀 회원 로그인 (PKCE 방식)
 * API: POST /api/v1/res/auth/login/custom-member-login
 */
export const customMemberLogin = async (params) => {
  const res = await authClient.post('login/custom-member-login', {
    json: {
      mbrLgnId: params.mbrLgnId,
      pswd: params.pswd,
      tenant: 'res-admin',
      codeChallenge: params.codeChallenge,
      state: params.state
    },
    throwHttpErrors: false
  })
  const data = await res.json().catch(() => ({}))
  return { ...data, __status: res.status }
}

/**
 * 인가 코드로 토큰 교환 (PKCE 방식)
 * API: POST /oauth2/token
 */
export const exchangeToken = async (params) => {
  const body = new URLSearchParams()
  body.append('grant_type', 'authorization_code')
  body.append('code', params.code)
  body.append('code_verifier', params.codeVerifier)
  body.append('redirect_uri', params.redirectUri)
  body.append('client_id', clientId)

  return await authServerClient
    .post(`oauth2/token?tenant=${encodeURIComponent(clientId)}`, {
      body: body,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })
    .json()
}

/**
 * 로그아웃
 * API: POST /api/v1/res/auth/logout/custom-member-logout?tenant=res-admin
 * @param {string} tenant - 테넌트 ('res-admin')
 * @returns {Promise}
 */
export const customMemberLogout = async (tenant = 'res-admin') => {
  return await authClient
    .post(`logout/custom-member-logout?tenant=${tenant}`)
    .json()
}

/**
 * 인증 서버 URL 반환
 * @returns {string}
 */
export const getAuthServerUrl = () => {
  return authServer
}

/**
 * 클라이언트 ID 반환
 * @returns {string}
 */
export const getClientId = () => {
  return clientId
}

const toFiniteSeconds = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.floor(value)
  if (typeof value === 'string') {
    const parsed = Number(value.trim())
    if (Number.isFinite(parsed)) return Math.floor(parsed)
  }
  return null
}

const extractRemainingSeconds = (body) => {
  if (!body || typeof body !== 'object') return null
  const root = body
  const data = body.data && typeof body.data === 'object' ? body.data : {}
  const candidates = [
    root.displayRemainingSeconds,
    root.remainingSeconds,
    root.serverRemainingSeconds,
    root.expires_in,
    root.expiresIn,
    data.displayRemainingSeconds,
    data.remainingSeconds,
    data.serverRemainingSeconds,
    data.expires_in,
    data.expiresIn
  ]
  for (const v of candidates) {
    const sec = toFiniteSeconds(v)
    if (sec !== null) return sec
  }
  return null
}

/**
 * Access Token 남은 TTL(초) 조회
 * API: POST /api/v1/res/auth/customMember/redis/user-token-info-check?refreshMode=AUTO
 * @param {Object} params
 * @param {string} params.accessToken - Access Token
 * @param {string} [params.refreshMode] - AUTO | FORCE
 * @param {string} [params.tenant] - tenant (default: res-admin)
 * @returns {Promise<number|null>}
 */
export const getAccessTokenRemainingSeconds = async (params) => {
  const info = await getAccessTokenInfoCheck(params)
  return info?.remainingSeconds ?? null
}

/**
 * Access Token TTL 조회 + 원본 응답 반환
 * API: POST /api/v1/res/auth/customMember/redis/user-token-info-check?refreshMode=AUTO
 * @param {Object} params
 * @param {string} params.accessToken - Access Token
 * @param {string} [params.refreshMode] - AUTO | FORCE
 * @param {string} [params.tenant] - tenant (default: res-admin)
 * @returns {Promise<{remainingSeconds: number|null, raw: any} | null>}
 */
export const getAccessTokenInfoCheck = async (params) => {
  const accessToken = params?.accessToken || ''
  if (!accessToken) return null
  const mode = (params?.refreshMode || 'AUTO').toUpperCase()
  const tenant = params?.tenant || 'res-admin'
  const raw = await authClient
    .post(
      `customMember/redis/user-token-info-check?refreshMode=${encodeURIComponent(mode)}&tenant=${encodeURIComponent(tenant)}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Refresh-Mode': mode
        }
      }
    )
    .json()
  return {
    remainingSeconds: extractRemainingSeconds(raw),
    raw
  }
}
